import {Instagram, Mail} from 'lucide-react';

const PinterestIcon = ({ size = 18 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M12.017 2c-5.523 0-10 4.477-10 10 0 4.237 2.636 7.855 6.356 9.312-.088-.791-.167-2.005.035-2.868.182-.78 1.172-4.97 1.172-4.97s-.299-.6-.299-1.486c0-1.39.806-2.428 1.81-2.428.853 0 1.265.64 1.265 1.408 0 .858-.545 2.14-.828 3.33-.236.995.499 1.807 1.48 1.807 1.777 0 3.144-1.874 3.144-4.58 0-2.393-1.72-4.068-4.176-4.068-2.845 0-4.516 2.135-4.516 4.34 0 .858.331 1.779.744 2.281a.3.3 0 0 1 .069.288c-.076.318-.246.995-.28 1.134-.043.183-.145.222-.334.134-1.249-.581-2.028-2.407-2.028-3.874 0-3.154 2.292-6.052 6.608-6.052 3.469 0 6.169 2.472 6.169 5.775 0 3.447-2.173 6.22-5.19 6.22-1.013 0-1.966-.526-2.292-1.147 0 0-.501 1.91-.623 2.379-.226.867-.836 1.955-1.244 2.618A10.001 10.001 0 0 0 22.017 12c0-5.523-4.477-10-10-10Z"/>
  </svg>
)
import { useState, useEffect } from 'react';
import { lumi } from '../lib/lumi';

const Footer = () => {
  const [instagramUrl, setInstagramUrl] = useState('https://www.instagram.com/amanaliya_art/');
  const [emailUrl, setEmailUrl] = useState('studio@amanaliya.com');
  const pinterestUrl = 'https://www.pinterest.com/AmanaliyaArt';

  useEffect(() => {
    const fetchContent = async (attempt = 0) => {
      try {
        const result = await lumi.entities.site_content.list({ filter: { page: 'footer' } });
        result?.list?.forEach((item: any) => {
          if (item.section === 'info') {
            if (item.content?.instagramUrl) setInstagramUrl(item.content.instagramUrl);
            if (item.content?.email) setEmailUrl(item.content.email);
          }
        });
      } catch (error) {
        if (attempt < 1) {
          setTimeout(() => fetchContent(attempt + 1), 1500);
        } else {
          console.error('Error loading footer content, using default Amanaliya Art contact details:', error);
        }
      }
    };
    fetchContent();
  }, []);

  return (
    <footer className="bg-[#FAF9F6] text-[#2C2C2C] pt-14">
      <div className="max-w-7xl mx-auto px-6">

        {/* Logo */}
        <div className="flex justify-center mb-6">
          <a href="/" aria-label="Amanaliya Art — Home">
            <img
              src="https://static.lumi.new/87/8702d61b4a30cdd7ae853f8d010d8c15.webp"
              alt="Amanaliya Art"
              className="h-14 w-auto object-contain opacity-90 hover:opacity-100 transition-opacity" />

          </a>
        </div>

        {/* Tagline */}
        <p
          className="text-center mb-12 text-lg"
          style={{ fontFamily: "'EB Garamond', Georgia, serif", fontStyle: 'italic', color: '#722F37' }}>

          Art for the heart
        </p>

        {/* Three columns */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-10 mb-12 pb-12">

          {/* Navigation */}
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-widest text-[#722F37] mb-4">Navigation</h4>
            <ul className="space-y-2.5 text-sm">
              {[
              { label: 'Gallery', href: '/gallery' },
              { label: 'About', href: '/about' },
              { label: 'Contact', href: '/contact' },
              { label: 'News & Events', href: '/actualites' }].
              map(({ label, href }) =>
              <li key={href}>
                  <a href={href} className="text-[#2C2C2C]/65 hover:text-[#722F37] transition-colors">
                    {label}
                  </a>
                </li>
              )}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-widest text-[#722F37] mb-4">Legal</h4>
            <ul className="space-y-2.5 text-sm">
              {[
              { label: 'Legal Notice', href: '/legal-notice.html' },
              { label: 'Terms & Conditions', href: '/terms.html' },
              { label: 'Shipping Policy', href: '/shipping.html' },
              { label: 'Returns Policy', href: '/returns.html' },
              { label: 'Privacy Policy', href: '/privacy-policy.html' },
              { label: 'Terms of Use', href: '/terms-of-use.html' }].
              map(({ label, href }) =>
              <li key={href}>
                  <a href={href} className="text-[#2C2C2C]/65 hover:text-[#722F37] transition-colors">
                    {label}
                  </a>
                </li>
              )}
            </ul>
          </div>

          {/* Follow me */}
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-widest text-[#722F37] mb-4">Follow me</h4>
            <div className="flex gap-3">
              <a
                href={instagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-[#722F37]/10 text-[#722F37] rounded-full flex items-center justify-center hover:bg-[#722F37] hover:text-[#FAF9F6] transition-colors"
                aria-label="Instagram">

                <Instagram size={18} />
              </a>
              <a
                href={pinterestUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-[#722F37]/10 text-[#722F37] rounded-full flex items-center justify-center hover:bg-[#722F37] hover:text-[#FAF9F6] transition-colors"
                aria-label="Pinterest">

                <PinterestIcon size={18} />
              </a>
              <a
                href={`mailto:${emailUrl}`}
                className="w-10 h-10 bg-[#722F37]/10 text-[#722F37] rounded-full flex items-center justify-center hover:bg-[#722F37] hover:text-[#FAF9F6] transition-colors"
                aria-label="Email">

                <Mail size={18} />
              </a>
            </div>
          </div>
        </div>

      </div>

      {/* Final band: solid burgundy background */}
      <div className="bg-[#722F37] text-[#FAF9F6] py-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center text-sm text-[#FAF9F6]/80">
            &copy; 2026 Amanaliya Art. All rights reserved.
          </div>

          {/* Cortex Digital credit */}
          <div className="cortex-credit">
            <span>Site créé par</span>
            <a href="https://cortexdigital.ch" target="_blank" rel="noopener">
              <img src="https://static.lumi.new/material/2d/2d7e87bf6733fa45e1f47a6b3f26cfce.png" alt="Cortex Digital" className="object-contain brightness-0 invert"></img>
            </a>
          </div>
        </div>
      </div>
    </footer>);

};

export default Footer;